import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { ThemeToggle } from '../components/ThemeToggle';
import { Icon } from '../components/ui';
import { useAuth } from '../lib/auth';
import { tr } from '../lib/i18n';

export const ADMIN_NAV = [
  { to: '/admin', label: 'Visão geral', icon: 'chart', end: true },
  { to: '/admin/ativacoes', label: 'Ativações', icon: 'check' },
  { to: '/admin/licencas', label: 'Licenças', icon: 'shield' },
  { to: '/admin/empresas', label: 'Empresas', icon: 'user' },
  { to: '/admin/assinaturas', label: 'Assinaturas', icon: 'clock' },
  { to: '/admin/usuarios', label: 'Usuários', icon: 'search' },
  { to: '/admin/planos', label: 'Planos', icon: 'dots' },
  { to: '/admin/categorias', label: 'Categorias', icon: 'plus' },
  { to: '/admin/avaliacoes', label: 'Avaliações', icon: 'star' },
  { to: '/admin/auditoria', label: 'Auditoria', icon: 'info' },
  { to: '/admin/configuracoes', label: 'Configurações', icon: 'settings' }
];

export function AdminLayout({ children }: { children: ReactNode }): JSX.Element {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen flex-col bg-bg">
      <header className="sticky top-0 z-40 bg-chrome text-chrome-ink shadow-md">
        <div className="mx-auto flex h-[68px] w-full max-w-content items-center gap-3 px-4">
          <button
            type="button"
            className="rounded-full p-2 hover:bg-white/10 lg:hidden"
            aria-label={menuOpen ? tr('Fechar menu') : tr('Abrir menu')}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <Icon name={menuOpen ? 'close' : 'menu'} size={22} />
          </button>
          <Link to="/admin" className="flex items-center gap-2">
            <Logo variant="light" size={38} wordSize={20} />
          </Link>
          <span className="hidden rounded-full border border-sand/70 px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-wide text-sand sm:inline-block">
            {tr("Administração")}
          </span>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden max-w-[16rem] truncate text-sm text-chrome-ink opacity-70 md:inline">{user?.email}</span>
            <Link to="/" className="hidden rounded-full px-4 py-2 text-sm font-medium hover:bg-white/10 sm:inline-block">
              {tr("Ver o site")}
            </Link>
            <button type="button" onClick={() => void logout().then(() => navigate('/'))} className="rounded-full px-4 py-2 text-sm font-medium hover:bg-white/10">
              {tr("Sair")}
            </button>
            <ThemeToggle tone="dark" />
          </div>
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-content flex-1 flex-col gap-6 px-4 py-6 lg:flex-row lg:gap-8 lg:py-8">
        <aside className={`${menuOpen ? 'block' : 'hidden'} w-full shrink-0 lg:sticky lg:top-[92px] lg:block lg:w-60 lg:self-start`} aria-label={tr("Menu da administração")}>
          <nav className="nl-card space-y-1 overflow-hidden p-2">
            {ADMIN_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-full px-4 py-3 text-sm font-semibold transition-all ${
                    isActive
                      ? 'bg-accent text-on-accent shadow-[0_10px_20px_-14px_var(--nl-accent)]'
                      : 'text-muted hover:bg-surface hover:text-graphite'
                  }`
                }
              >
                <Icon name={item.icon} size={18} />
                {tr(item.label)}
              </NavLink>
            ))}
          </nav>
        </aside>

        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </div>
  );
}
